import React from 'react';
import { cn } from '@/lib/utils';

export interface SwipeAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void | Promise<void>;
  variant?: 'destructive' | 'default';
  ariaLabel?: string;
}

interface SwipeToRevealProps {
  actions: SwipeAction[];
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

/** Width of one revealed action button. Total drawer width = N × this. */
const ACTION_WIDTH = 80;
/** Drag past this fraction of drawer width → snap open. Otherwise snap closed. */
const TRIGGER_FRACTION = 0.5;
/** Pixel deadzone before deciding the gesture is horizontal vs vertical. */
const DIRECTION_THRESHOLD = 8;

/** Custom event used to enforce single-open: when one card opens, every
 *  other instance closes itself. Mirrors iOS Mail. */
const OPENED_EVENT = 'swipe-to-reveal:opened';

/** iOS-style swipe-to-reveal: drag the card content left to expose
 *  contextual actions (Delete, Archive, etc.) underneath.
 *
 *   - Touch and mouse both supported via pointer events.
 *   - Vertical scroll is preserved: a gesture only "owns" the swipe
 *     once it's measurably more horizontal than vertical.
 *   - Only one row open at a time across the whole page (event-bus).
 *   - Tap on the card body or outside the row closes the drawer
 *     without firing the child's own click handler.
 *   - Action buttons size to the configured `ACTION_WIDTH` each; pass
 *     2-3 actions max for thumb-friendly targets.
 */
export function SwipeToReveal({ actions, children, className, disabled }: SwipeToRevealProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [translate, setTranslate] = React.useState(0);
  const [isOpen, setIsOpen] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);

  const drawerWidth = ACTION_WIDTH * actions.length;
  const triggerThreshold = drawerWidth * TRIGGER_FRACTION;

  // Drag state lives in a ref so move/up handlers see live values
  // without stale-closure issues from setState.
  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    direction: 'undecided' | 'horizontal' | 'vertical';
    startedOpen: boolean;
    pointerId: number;
  } | null>(null);

  // After a horizontal gesture finishes, the browser may still fire a
  // synthetic `click` on the foreground (because pointerup happened on
  // the same element as pointerdown). We intercept that one click with
  // this flag so the underlying card's `onClick` doesn't fire.
  const swallowNextClickRef = React.useRef(false);

  const close = React.useCallback(() => {
    setIsOpen(false);
    setTranslate(0);
  }, []);

  // Listen for "another instance opened" → close ourselves.
  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === containerRef.current) return; // it was us
      close();
    };
    window.addEventListener(OPENED_EVENT, handler);
    return () => window.removeEventListener(OPENED_EVENT, handler);
  }, [close]);

  // Click-outside closes the drawer (defer-attach so the same gesture
  // that opened doesn't immediately close it).
  React.useEffect(() => {
    if (!isOpen) return;
    let attached: ((e: PointerEvent) => void) | null = null;
    const handler = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    };
    const t = window.setTimeout(() => {
      document.addEventListener('pointerdown', handler);
      attached = handler;
    }, 0);
    return () => {
      window.clearTimeout(t);
      if (attached) document.removeEventListener('pointerdown', attached);
    };
  }, [isOpen, close]);

  function onPointerDown(e: React.PointerEvent) {
    if (disabled) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      direction: 'undecided',
      startedOpen: isOpen,
      pointerId: e.pointerId,
    };
  }

  function onPointerMove(e: React.PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (d.direction === 'undecided') {
      if (Math.abs(dx) < DIRECTION_THRESHOLD && Math.abs(dy) < DIRECTION_THRESHOLD) return;
      d.direction = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      if (d.direction === 'horizontal') {
        try { (e.target as HTMLElement).setPointerCapture(d.pointerId); } catch { /* element gone */ }
        setDragging(true);
      }
    }
    if (d.direction !== 'horizontal') return;

    const baseOffset = d.startedOpen ? -drawerWidth : 0;
    let next = baseOffset + dx;
    if (next > 0) next = 0;
    if (next < -drawerWidth) next = -drawerWidth;
    setTranslate(next);
  }

  function onPointerUp(e: React.PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    setDragging(false);
    if (d.direction !== 'horizontal') return;

    swallowNextClickRef.current = true;
    // Belt-and-braces: clear the swallow flag after one tick if no
    // click ever fires (e.g. user lifted finger outside any element).
    window.setTimeout(() => { swallowNextClickRef.current = false; }, 300);

    const dx = e.clientX - d.startX;
    const baseOffset = d.startedOpen ? -drawerWidth : 0;
    const finalOffset = baseOffset + dx;
    const shouldOpen = finalOffset < -triggerThreshold;
    if (shouldOpen) {
      setTranslate(-drawerWidth);
      setIsOpen(true);
      window.dispatchEvent(new CustomEvent(OPENED_EVENT, { detail: containerRef.current }));
    } else {
      setTranslate(0);
      setIsOpen(false);
    }
  }

  // Capture-phase click handler: swallow the click that the browser
  // fires after a horizontal swipe so the inner card's onClick (which
  // would open the edit dialog) doesn't run.
  function onClickCapture(e: React.MouseEvent) {
    if (swallowNextClickRef.current) {
      swallowNextClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  }

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-hidden touch-pan-y', className)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={onClickCapture}
    >
      {/* Action drawer — sits behind the foreground, revealed as the
          card translates left. Always rendered so the user can preview
          a peek of color while dragging. */}
      <div className="absolute inset-y-0 right-0 flex">
        {actions.map((a, i) => (
          <button
            key={i}
            type="button"
            aria-label={a.ariaLabel ?? a.label}
            style={{ width: ACTION_WIDTH }}
            className={cn(
              'h-full flex flex-col items-center justify-center gap-1 text-xs font-medium text-white',
              a.variant === 'destructive' ? 'bg-red-600' : 'bg-primary',
            )}
            onClick={async () => {
              close();
              await a.onClick();
            }}
          >
            {a.icon}
            <span>{a.label}</span>
          </button>
        ))}
      </div>

      {/* Foreground card body. Translates left as the user drags. */}
      <div
        className="relative bg-background"
        style={{
          transform: `translateX(${translate}px)`,
          transition: dragging ? 'none' : 'transform 200ms ease-out',
        }}
      >
        {children}
      </div>

      {/* Tap-to-close overlay, sized to cover only the visible
          foreground (leaving the drawer's actions clickable). Mounted
          only when open so taps on closed cards still reach the card
          body normally. */}
      {isOpen && (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          className="absolute inset-y-0 left-0 z-10 cursor-default"
          style={{ right: drawerWidth }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); }}
        />
      )}
    </div>
  );
}
